import {
  Children,
  isValidElement,
  type FC,
  type ReactElement,
  type ReactNode,
} from 'react';

import { cn } from '@/components/TailwindComponents/lib/utils';

interface PageHeaderProps {
  title: string;
  children?: ReactNode;
  className?: string;
  backButton?: ReactElement;
}

export const PageHeader: FC<PageHeaderProps> = ({
  title,
  children,
  className,
  backButton,
}) => {
  const leftActions = Children.toArray(children).filter(
    (child) =>
      isValidElement(child) &&
      typeof child.type === 'function' &&
      (child.type as { displayName?: string })?.displayName ===
        'PageHeaderAction' &&
      child.props.position === 'left',
  );
  const centerActions = Children.toArray(children).filter(
    (child) =>
      isValidElement(child) &&
      typeof child.type === 'function' &&
      (child.type as { displayName?: string })?.displayName ===
        'PageHeaderAction' &&
      child.props.position === 'center',
  );
  const rightActions = Children.toArray(children).filter(
    (child) =>
      isValidElement(child) &&
      typeof child.type === 'function' &&
      (child.type as { displayName?: string })?.displayName ===
        'PageHeaderAction' &&
      child.props.position === 'right',
  );

  return (
    <>
      <div
        className={cn(
          'flex items-center py-4 px-6 border-b border-gray-200 dark:border-[#192932] bg-white dark:bg-[#0B1922] text-gray-900 dark:text-gray-100',
          className,
        )}
      >
        {/* Left section with back button, title and left actions */}
        <div className="flex items-center gap-2 flex-1">
          {backButton ? backButton : null}
          <h2 className="text-xl font-semibold text-inherit">{title}</h2>
          {leftActions.length > 0 && (
            <div className="flex items-center ml-4">{leftActions}</div>
          )}
        </div>

        {/* Center section */}
        {centerActions.length > 0 && (
          <div className="flex items-center justify-center flex-1">
            {centerActions}
          </div>
        )}

        {/* Right section */}
        <div className="flex items-center justify-end flex-1">
          {rightActions}
        </div>
      </div>
    </>
  );
};

PageHeader.displayName = 'PageHeader';
